import {
  DEFAULT_ROUTING_TRANSPORT_PROFILE,
  type LatLng,
  type LngLatCoord,
  type RoutePathOption,
  type RoutePlan,
  type RouteSegmentPlan,
  type RoutingProvider,
  type RoutingRequestOptions,
  type RoutingTransportProfile,
} from '@gpx-spa/geo-domain'
import { decodeGoogleRoutesEncodedPolylineMeasured } from '../geometry/google-encoded-polyline.js'

export interface GoogleRoutesRoutingProviderOptions {
  getApiKey: () => string
}

/** Google Routes API v2 `computeRoutes`. */
const ROUTES_URL = 'https://routes.googleapis.com/directions/v2:computeRoutes'

function durationToSeconds(d: unknown): number | undefined {
  if (d == null) return undefined
  if (typeof d === 'number' && Number.isFinite(d)) return Math.round(d)
  if (typeof d === 'string') {
    const m = /^(\d+(?:\.\d+)?)s$/.exec(d)
    if (m) return Math.round(parseFloat(m[1]))
  }
  if (typeof d === 'object' && d !== null && 'seconds' in d) {
    const s = (d as { seconds?: string | number }).seconds
    if (typeof s === 'string') return parseInt(s, 10)
    if (typeof s === 'number') return Math.round(s)
  }
  return undefined
}

function distanceToMeters(d: unknown): number | undefined {
  if (d == null) return undefined
  if (typeof d === 'number' && Number.isFinite(d)) return Math.round(d)
  if (typeof d === 'string') {
    const n = parseInt(d, 10)
    if (Number.isFinite(n)) return n
  }
  return undefined
}

export type GoogleComputeRoutesLeg = {
  distanceMeters?: unknown
  duration?: unknown
  polyline?: { encodedPolyline?: string }
}

export type GoogleComputeRoutesRoute = {
  distanceMeters?: unknown
  duration?: unknown
  polyline?: { encodedPolyline?: string }
  legs?: GoogleComputeRoutesLeg[]
}

/** Body shape of `directions/v2:computeRoutes` JSON (minimal fields used for polylines). */
export type GoogleComputeRoutesResponse = {
  routes?: GoogleComputeRoutesRoute[]
}

/**
 * Maps our transport profile to Google `travelMode`.
 *
 * There is **no** `TRUCK` (or HGV) in the API enum — only `DRIVE` (passenger car), `WALK`, `BICYCLE`,
 * `TWO_WHEELER`, `TRANSIT`. See:
 * - https://developers.google.com/maps/documentation/routes/reference/rest/v2/RouteTravelMode
 * - https://developers.google.com/maps/documentation/routes/vehicles
 */
function googleTravelMode(profile: RoutingTransportProfile | undefined): string {
  const p = profile ?? DEFAULT_ROUTING_TRANSPORT_PROFILE
  switch (p) {
    case 'walking':
      return 'WALK'
    case 'cycling':
      return 'BICYCLE'
    default:
      return 'DRIVE'
  }
}

/** `TRAFFIC_AWARE` only for motor modes; `TRAFFIC_UNAWARE` for explicit `driving` without traffic. */
function googleRoutingPreference(profile: RoutingTransportProfile | undefined): string | undefined {
  const p = profile ?? DEFAULT_ROUTING_TRANSPORT_PROFILE
  if (p === 'walking' || p === 'cycling') return undefined
  if (p === 'driving') return 'TRAFFIC_UNAWARE'
  return 'TRAFFIC_AWARE'
}

function decodeRoutePolyline(enc: string | undefined): {
  coordinates: LngLatCoord[]
  decodeMs: number
  vertexCount: number
} | null {
  if (!enc) return null
  const measured = decodeGoogleRoutesEncodedPolylineMeasured(enc)
  if (measured.coordinates.length === 0) {
    return {
      coordinates: [],
      decodeMs: measured.decodeMs,
      vertexCount: measured.vertexCount,
    }
  }
  return measured
}

function googlePathOption(
  id: string,
  coordinates: LngLatCoord[],
  distanceMeters: unknown,
  duration: unknown
): RoutePathOption {
  return {
    id,
    coordinates,
    meta: {
      distance_m: distanceToMeters(distanceMeters),
      duration_s: durationToSeconds(duration),
      provider: 'google',
    },
  }
}

function googleRoutesToSegments(
  routes: GoogleComputeRoutesRoute[],
  stopCount: number
): {
  segments: RouteSegmentPlan[]
  polylineDecodeMs: number
  polylineDecodeVertexCount: number
} {
  if (stopCount < 2 || routes.length === 0) {
    return { segments: [], polylineDecodeMs: 0, polylineDecodeVertexCount: 0 }
  }

  let polylineDecodeMs = 0
  let polylineDecodeVertexCount = 0

  if (stopCount === 2) {
    const paths: RoutePathOption[] = []
    for (const [idx, r] of routes.entries()) {
      const decoded = decodeRoutePolyline(r.polyline?.encodedPolyline)
      if (!decoded) continue
      polylineDecodeMs += decoded.decodeMs
      polylineDecodeVertexCount += decoded.vertexCount
      if (decoded.coordinates.length < 2) continue
      paths.push(
        googlePathOption(
          idx === 0 ? 'primary' : `alternative_${idx}`,
          decoded.coordinates,
          r.distanceMeters,
          r.duration
        )
      )
    }
    return {
      segments: [
        {
          sequence: 0,
          fromStopIndex: 0,
          toStopIndex: 1,
          paths,
        },
      ],
      polylineDecodeMs,
      polylineDecodeVertexCount,
    }
  }

  const legs = routes[0]?.legs ?? []
  const segments: RouteSegmentPlan[] = []
  for (let i = 0; i < legs.length; i++) {
    const leg = legs[i]!
    const decoded = decodeRoutePolyline(leg.polyline?.encodedPolyline)
    const paths: RoutePathOption[] = []
    if (decoded) {
      polylineDecodeMs += decoded.decodeMs
      polylineDecodeVertexCount += decoded.vertexCount
      if (decoded.coordinates.length >= 2) {
        paths.push(
          googlePathOption('primary', decoded.coordinates, leg.distanceMeters, leg.duration)
        )
      }
    }
    segments.push({
      sequence: i,
      fromStopIndex: i,
      toStopIndex: i + 1,
      paths,
    })
  }

  return { segments, polylineDecodeMs, polylineDecodeVertexCount }
}

/**
 * Maps a cached `computeRoutes` JSON response to {@link RoutePlan} (same logic as the live Google adapter).
 * Use for fixtures / perf tests without calling the API.
 */
export function routePlanFromGoogleComputeRoutesResponse(
  data: GoogleComputeRoutesResponse,
  stopCount: number,
  transportProfile: RoutingTransportProfile = DEFAULT_ROUTING_TRANSPORT_PROFILE
): RoutePlan {
  const routes = data.routes ?? []
  const { segments, polylineDecodeMs, polylineDecodeVertexCount } = googleRoutesToSegments(
    routes,
    stopCount
  )
  return {
    segments,
    meta: {
      provider: 'google',
      transportProfile,
      polylineDecodeMs,
      polylineDecodeVertexCount,
    },
  }
}

/**
 * {@link RoutingProvider} via Google Routes API v2 (`computeRoutes`). Requires **Routes API** enabled for the key.
 *
 * - Encoded polylines: {@link decodeGoogleRoutesEncodedPolylineMeasured} / {@link decodeGoogleRoutesEncodedPolyline} in **`geometry/google-encoded-polyline.ts`**; decode timings in {@link RoutePlan.meta}.
 * - **Traffic**: `driving-traffic` → `routingPreference: TRAFFIC_AWARE`; `driving` → `TRAFFIC_UNAWARE`.
 *
 * @see https://developers.google.com/maps/documentation/routes/compute_route_directions
 * @see https://developers.google.com/maps/documentation/routes/reference/rest/v2/RouteTravelMode
 * @see https://developers.google.com/maps/documentation/routes/vehicles
 */
export function createGoogleRoutesRoutingProvider(
  options: GoogleRoutesRoutingProviderOptions
): RoutingProvider {
  return {
    async getRoute(stops: LatLng[], opts?: RoutingRequestOptions): Promise<RoutePlan> {
      const apiKey = options.getApiKey().trim()
      const profile = opts?.transportProfile ?? DEFAULT_ROUTING_TRANSPORT_PROFILE
      if (!apiKey || stops.length < 2) {
        return { segments: [], meta: { provider: 'google', transportProfile: profile } }
      }
      const slice = stops.slice(0, 25)
      const origin = slice[0]
      const destination = slice[slice.length - 1]

      const travelMode = googleTravelMode(opts?.transportProfile)
      const routingPreference = googleRoutingPreference(opts?.transportProfile)

      const body: Record<string, unknown> = {
        origin: {
          location: { latLng: { latitude: origin.lat, longitude: origin.lng } },
        },
        destination: {
          location: { latLng: { latitude: destination.lat, longitude: destination.lng } },
        },
        travelMode,
      }
      if (routingPreference) {
        body.routingPreference = routingPreference
      }
      if (slice.length > 2) {
        body.intermediates = slice.slice(1, -1).map((s) => ({
          location: { latLng: { latitude: s.lat, longitude: s.lng } },
        }))
      }
      if (slice.length === 2 && travelMode === 'DRIVE') {
        body.computeAlternativeRoutes = true
      }

      const res = await fetch(ROUTES_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Goog-Api-Key': apiKey,
          'X-Goog-FieldMask':
            'routes.duration,routes.distanceMeters,routes.polyline.encodedPolyline,routes.legs.duration,routes.legs.distanceMeters,routes.legs.polyline.encodedPolyline',
        },
        body: JSON.stringify(body),
        signal: opts?.signal,
      })
      if (!res.ok) {
        const errText = (await res.text()).slice(0, 500)
        return {
          segments: [],
          meta: {
            provider: 'google',
            transportProfile: profile,
            error: `${res.status} ${res.statusText}: ${errText}`,
          },
        }
      }
      const data = (await res.json()) as GoogleComputeRoutesResponse
      return routePlanFromGoogleComputeRoutesResponse(data, slice.length, profile)
    },
  }
}
